import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone } from 'lucide-react';
import './LegalPage.css';

const LAST_UPDATED  = 'February 28, 2026';
const SITE_NAME     = 'Rajesh R – AI Platform Optimization Expert';
const CONTACT_EMAIL = 'info@rajeshrnair.com';
const CONTACT_PHONE = '+91 79070 38984';
const WA_LINK = 'https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20have%20a%20privacy%20related%20query.';

export default function PrivacyPolicy() {
  const sections = [
    {
      num: 1,
      title: 'Who We Are',
      content: (
        <p>
          This Privacy Policy applies to the website operated by Rajesh R, an AI Platform
          Optimization specialist based in Trivandrum, Kerala, India. When we say "we",
          "us", or "our", we refer to Rajesh R and this website. When we say "you" or
          "your", we refer to any person visiting or using this website.
        </p>
      ),
    },
    {
      num: 2,
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect only the minimum information necessary to provide our services:</p>
          <p><strong>When you use the Free Site Audit Tool:</strong></p>
          <ul>
            <li><strong>Website URL</strong> — The URL you submit for analysis. This is sent to a third-party CORS proxy service to fetch the publicly accessible HTML. We do not store this URL on our servers.</li>
            <li><strong>Gemini API Key (optional)</strong> — If you choose to enter your own Google Gemini API key, it is stored exclusively in your browser's <code>localStorage</code>. It is never transmitted to our servers or stored anywhere by us.</li>
          </ul>
          <p><strong>When you contact us (WhatsApp, Email, Contact Form):</strong></p>
          <ul>
            <li>Your name (if provided)</li>
            <li>Your phone number or email address</li>
            <li>The message or enquiry content you share with us</li>
          </ul>
          <p><strong>Automatically collected data (standard web analytics):</strong></p>
          <ul>
            <li>Browser type and version</li>
            <li>Pages visited and time spent</li>
            <li>Referring URL</li>
            <li>General geographic location (country/city level only)</li>
          </ul>
        </>
      ),
    },
    {
      num: 3,
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect solely for the following purposes:</p>
          <ul>
            <li>To run the Free Site Audit Tool and display results to you in your browser.</li>
            <li>To respond to your enquiries, messages, and service requests.</li>
            <li>To improve the quality and functionality of our website and tools.</li>
            <li>To understand how visitors use our website so we can improve the user experience.</li>
            <li>To send you service-related updates if you have engaged us for paid work (no unsolicited marketing without consent).</li>
          </ul>
          <p>
            We do <strong>not</strong> sell, rent, or trade your personal information to
            any third party for marketing or any other commercial purpose.
          </p>
        </>
      ),
    },
    {
      num: 4,
      title: 'Third-Party Services We Use',
      content: (
        <>
          <p>
            The Free Site Audit Tool relies on the following third-party services.
            Each has its own privacy policy which we encourage you to review:
          </p>
          <ul>
            <li>
              <strong>allorigins.win / corsproxy.io</strong> — CORS proxy services used to fetch
              publicly accessible HTML from the URL you submit. The URL you enter is sent to
              these services. We do not control their data practices.
            </li>
            <li>
              <strong>Google Gemini API</strong> — If you provide your API key, your website's
              content (title, meta description, headings, body text) is sent to Google's Gemini
              API to generate AI insights. This is governed by
              Google's Privacy Policy and API Terms of Service.
            </li>
            <li>
              <strong>Firebase Hosting (Google)</strong> — This website is hosted on Firebase,
              a Google service. Standard hosting logs (IP address, pages requested, timestamps)
              may be retained by Google as part of their infrastructure.
            </li>
          </ul>
        </>
      ),
    },
    {
      num: 5,
      title: 'Cookies & Local Storage',
      content: (
        <>
          <p>
            This website uses <strong>browser localStorage</strong> — not cookies — to
            save your Gemini API key locally on your own device if you choose to enter one.
            This data never leaves your device and can be cleared by you at any time by
            clearing your browser's site data.
          </p>
          <p>
            Firebase Hosting may set standard operational cookies for session management
            and security purposes. No advertising or tracking cookies are placed by us.
          </p>
        </>
      ),
    },
    {
      num: 6,
      title: 'Data Retention',
      content: (
        <p>
          We do not operate databases that store your personal information from the
          Free Site Audit Tool. URLs you submit and audit results are processed in
          real time and displayed only in your browser — they are never written to
          any database or file on our servers. Contact enquiries sent via WhatsApp
          or email are retained only for as long as necessary to respond to and
          complete your request.
        </p>
      ),
    },
    {
      num: 7,
      title: 'Children\'s Privacy',
      content: (
        <p>
          This website is not directed at children under the age of 13. We do not
          knowingly collect personal information from children. If you believe a
          child has provided us with personal information, please contact us
          immediately and we will take steps to remove that information.
        </p>
      ),
    },
    {
      num: 8,
      title: 'Your Rights',
      content: (
        <>
          <p>
            Under applicable Indian data protection laws and international best practice,
            you have the right to:
          </p>
          <ul>
            <li>Request access to any personal information we hold about you.</li>
            <li>Request correction of any inaccurate personal information.</li>
            <li>Request deletion of your personal information from our records.</li>
            <li>Withdraw consent for us to use your information (where consent is the basis).</li>
            <li>Object to how we use your information.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us via email or WhatsApp. We will
            respond within 30 days.
          </p>
        </>
      ),
    },
    {
      num: 9,
      title: 'Security',
      content: (
        <p>
          We implement appropriate technical and organisational measures to protect your
          information from unauthorised access, alteration, disclosure, or destruction.
          Our website is served over HTTPS. However, no method of transmission over the
          internet is 100% secure. We cannot guarantee absolute security and you use
          this website at your own risk.
        </p>
      ),
    },
    {
      num: 10,
      title: 'Changes to This Policy',
      content: (
        <p>
          We may update this Privacy Policy from time to time. We will notify you of
          significant changes by updating the "Last Updated" date on this page. We
          encourage you to review this page periodically. Your continued use of this
          website after any changes constitutes acceptance of the updated policy.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Rajesh R – AI Platform Optimization Expert</title>
        <meta name="description" content="Privacy Policy for Rajesh R's website and Free Site Audit Tool. Learn how we handle your data, third-party services, and your rights." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="legal-page">

        {/* Hero */}
        <section className="legal-hero">
          <div className="container">
            <div className="legal-badge"><Shield size={12} /> Legal</div>
            <h1 className="legal-hero-title">Privacy Policy</h1>
            <p className="legal-hero-sub">
              We respect your privacy. This page explains what data we collect, how we use it,
              and how we protect it.
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="legal-body">
          <div className="container">
            <div className="legal-content">

              {/* Sections */}
              {sections.map(sec => (
                <div key={sec.num} className="legal-section">
                  <div className="legal-section-header">
                    <span className="legal-section-num">{sec.num}</span>
                    <h2>{sec.title}</h2>
                  </div>
                  {sec.content}
                </div>
              ))}

              {/* Contact */}
              <div className="legal-contact-box">
                <h3>Privacy Questions or Concerns?</h3>
                <p>
                  If you have any questions about this Privacy Policy or how we handle
                  your data, please contact us directly.
                </p>
                <div className="legal-contact-links">
                  <a href={WA_LINK} target="_blank" rel="noreferrer noopener" className="legal-contact-link primary">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Us
                  </a>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="legal-contact-link secondary">
                    <Mail size={14} /> {CONTACT_EMAIL}
                  </a>
                  <a href={`tel:${CONTACT_PHONE.replace(/\s/g,'')}`} className="legal-contact-link secondary">
                    <Phone size={14} /> {CONTACT_PHONE}
                  </a>
                </div>
              </div>

              <p className="legal-updated">
                Last Updated: {LAST_UPDATED} · {SITE_NAME} ·{' '}
                <Link to="/terms-and-conditions" style={{ color: '#8b5cf6' }}>Terms & Conditions</Link>
              </p>

            </div>
          </div>
        </section>

      </div>
    </>
  );
}
